import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../services/config';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Add,
  MoreVert,
  LocationOn,
  CalendarToday,
  Person,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface Project {
  _id: string;
  name: string;
  description: string;
  status: 'planning' | 'active' | 'completed' | 'on-hold';
  startDate: string;
  endDate: string;
  location: string;
  manager: {
    firstName: string;
    lastName: string;
  };
  budget: number;
  progress: number;
}

const Projects: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const canCreate = user?.role === 'admin' || user?.role === 'project_manager';

  // Helper to validate MongoDB ObjectId strings
  const isValidObjectId = (id: unknown): boolean => {
    return typeof id === 'string' && /^[a-fA-F0-9]{24}$/.test(id);
  };

  useEffect(() => {
    const mapProject = (p: any): Project => {
      const statusRaw = p?.status || 'planning';
      const status = statusRaw === 'on_hold' ? 'on-hold' : (statusRaw as any);
      const loc = p?.location;
      const locStr = typeof loc === 'string'
        ? loc
        : loc?.address
          ? [loc.address.street, loc.address.city, loc.address.state].filter(Boolean).join(', ')
          : '';
      const mgr = p?.manager;
      let firstName = '';
      let lastName = '';
      if (typeof mgr === 'string') {
        firstName = 'Manager';
        lastName = '';
      } else if (mgr?.firstName || mgr?.lastName) {
        firstName = mgr.firstName || '';
        lastName = mgr.lastName || '';
      } else if (mgr?.name) {
        const parts = String(mgr.name).split(' ');
        firstName = parts[0] || '';
        lastName = parts.slice(1).join(' ') || '';
      }
      const budgetVal = typeof p?.budget === 'number'
        ? p.budget
        : (p?.budget?.totalBudget ?? p?.budget?.total ?? 0);
      return {
        _id: p?._id || p?.id || '',
        name: p?.name || p?.title || 'Untitled Project',
        description: p?.description || '',
        status,
        startDate: p?.startDate || p?.timeline?.startDate || new Date().toISOString(),
        endDate: p?.endDate || p?.timeline?.endDate || new Date().toISOString(),
        location: locStr,
        manager: { firstName, lastName },
        budget: Number(budgetVal) || 0,
        progress: typeof p?.progress === 'number' ? p.progress : 0,
      };
    };

    const fetchProjects = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/projects`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error('Failed to fetch projects');
        const payload = await res.json();
        const items: any[] = Array.isArray(payload?.data)
          ? payload.data
          : (Array.isArray(payload?.projects) ? payload.projects : []);
        const mapped: Project[] = items.map(mapProject);
        const valid: Project[] = mapped.filter((p: Project) => p._id && isValidObjectId(p._id));
        setProjects(valid);
      } catch (err: any) {
        console.error('Error loading projects', err);
        setError(err?.message || 'Failed to load projects');
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, projectId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedProject(projectId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProject(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'planning':
        return 'warning';
      case 'completed':
        return 'info';
      case 'on-hold':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Box sx={{ mb: 2 }}>
          <Typography color="error" variant="body2">{error}</Typography>
        </Box>
      )}
      {loading && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2">Loading projects...</Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight="bold">
          Projects
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          sx={{ borderRadius: 2 }}
          onClick={() => {
            console.log('[Projects] New Project button clicked', { canCreate });
            if (!canCreate) {
              console.log('[Projects] Click blocked: user lacks permission to create projects');
              return;
            }
            console.log('[Projects] Navigating to /projects/create');
            navigate('/projects/create');
          }}
          disabled={!canCreate}
        >
          New Project
        </Button>
      </Box>

      {!loading && !error && projects.length === 0 && (
        <Box sx={{
          border: '1px dashed #ccc',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          color: 'text.secondary',
          mb: 3
        }}>
          <Typography variant="h6" gutterBottom>
            No projects yet
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {canCreate
              ? 'Create your first project to get started.'
              : 'You don’t have permission to create projects. Contact an administrator.'}
          </Typography>
          {canCreate && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                console.log('[Projects] Empty state Create Project clicked');
                console.log('[Projects] Navigating to /projects/create');
                navigate('/projects/create');
              }}
            >
              Create Project
            </Button>
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {projects.map((project) => (
          <Card 
            key={project._id} 
            sx={{ 
              borderRadius: 2, 
              boxShadow: 2, 
              cursor: 'pointer',
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
            onClick={() => navigate(`/projects/${project._id}`)}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {project.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {project.description}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                    <Chip
                      label={project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                      color={getStatusColor(project.status) as any}
                      size="small"
                    />
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOn fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {project.location}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CalendarToday fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {new Date(project.startDate).toLocaleDateString()} - {new Date(project.endDate).toLocaleDateString()}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Person fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {project.manager.firstName} {project.manager.lastName}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight="medium">
                      Budget: {formatCurrency(project.budget)}
                    </Typography>
                    <Typography variant="body2" color="primary" fontWeight="medium">
                      {project.progress}% Complete
                    </Typography>
                  </Box>
                </Box>
                
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuClick(e, project._id);
                  }}
                  sx={{ ml: 2 }}
                >
                  <MoreVert />
                </IconButton>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMenuClose}>View Details</MenuItem>
        <MenuItem onClick={handleMenuClose}>Edit Project</MenuItem>
        <MenuItem onClick={handleMenuClose}>View Reports</MenuItem>
        <MenuItem onClick={handleMenuClose}>Manage Team</MenuItem>
      </Menu>
    </Box>
  );
};

export default Projects;